import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const SubscriptionPlanChart = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const planData = [
    { name: 'Enterprise', value: 45, revenue: 1680000, color: '#1e40af', customers: 156 },
    { name: 'Professional', value: 35, revenue: 924000, color: '#059669', customers: 289 },
    { name: 'Starter', value: 15, revenue: 168000, color: '#f59e0b', customers: 445 },
    { name: 'Trial', value: 5, revenue: 0, color: '#94a3b8', customers: 78 }
  ];

  const totalRevenue = planData.reduce((sum, plan) => sum + plan.revenue, 0);
  const totalCustomers = planData.reduce((sum, plan) => sum + plan.customers, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevation-3">
          <p className="text-text-primary font-medium mb-2">{data.name} Plan</p>
          <div className="space-y-1">
            <p className="text-text-secondary text-sm">
              Revenue: <span className="text-text-primary font-medium">${data.revenue.toLocaleString()}</span>
            </p>
            <p className="text-text-secondary text-sm">
              Customers: <span className="text-text-primary font-medium">{data.customers}</span>
            </p>
            <p className="text-text-secondary text-sm">
              Share: <span className="text-text-primary font-medium">{data.value}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 dashboard-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-1">Subscription Distribution</h3>
          <p className="text-text-secondary text-sm">Revenue by subscription plan</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="PieChart" size={16} className="text-text-secondary" />
          <span className="text-text-secondary text-sm">Live Data</span>
        </div>
      </div>

      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={planData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              onMouseEnter={(data) => setSelectedPlan(data.name)}
              onMouseLeave={() => setSelectedPlan(null)}
            >
              {planData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                  stroke={selectedPlan === entry.name ? '#ffffff' : 'transparent'}
                  strokeWidth={selectedPlan === entry.name ? 2 : 0}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3">
        {planData.map((plan) => (
          <div 
            key={plan.name}
            className={`flex items-center justify-between p-3 rounded-lg transition-all duration-150 cursor-pointer ${
              selectedPlan === plan.name ? 'bg-muted/50' : 'hover:bg-muted/30'
            }`}
            onMouseEnter={() => setSelectedPlan(plan.name)}
            onMouseLeave={() => setSelectedPlan(null)}
          >
            <div className="flex items-center space-x-3">
              <div 
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: plan.color }}
              />
              <div>
                <p className="text-text-primary font-medium text-sm">{plan.name}</p>
                <p className="text-text-secondary text-xs">{plan.customers} customers</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-text-primary font-medium text-sm">
                ${plan.revenue.toLocaleString()}
              </p>
              <p className="text-text-secondary text-xs">{plan.value}%</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Total Revenue</p>
            <p className="text-text-primary font-bold text-lg">
              ${totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Total Customers</p>
            <p className="text-text-primary font-bold text-lg">
              {totalCustomers.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanChart;